import React, { useState, useMemo } from 'react';
import { Transition } from '@headlessui/react';
import { ChevronDownIcon, ChevronUpIcon } from '@heroicons/react/outline';

import LoadingSpinner from 'components/LoadingSpinner';

import Pagination from './Pagination';
import { ITable, ITableSort } from './interfaces';

export default function Table({
  key = '_id',
  isLoading = false,
  loadingText = 'Cargando',
  amountPerPage = 10,
  initialSort,
  columns,
  data,
  totalDocs,
  currentPage,
  onPageChange,
}: ITable) {
  const [page, setPage] = useState(1);
  const [sort, setSort] = useState<ITableSort>(
    initialSort || {
      key: columns[0].key,
      order: 'desc',
    }
  );

  const processedRows = useMemo(() => {
    if (!data) return [];
    const order = sort.order === 'desc' ? -1 : 1;
    const index = columns.findIndex(
      (column) => column.key === sort.key || column.dataKey === sort.key
    );
    if (currentPage) {
      return [...data]?.sort((a, b) => {
        const valueA =
          columns[index]?.sortValue?.(a) !== undefined
            ? columns[index]?.sortValue?.(a)
            : a[sort.key] || '';
        const valueB =
          columns[index]?.sortValue?.(b) !== undefined
            ? columns[index]?.sortValue?.(b)
            : b[sort.key] || '';

        console.log({ valueA, valueB, order });

        if (valueA === '') return -1 * order;
        if (valueA < valueB) return -1 * order;
        if (valueA > valueB) return 1 * order;
        return 0;
      });
    }

    return [...data]
      ?.sort((a, b) => {
        const valueA = columns[index]?.sortValue?.(a) || a[sort.key];
        const valueB = columns[index]?.sortValue?.(b) || b[sort.key];

        if (valueA < valueB) return -1 * order;
        if (valueA > valueB) return 1 * order;
        return 0;
      })
      ?.slice(amountPerPage * (page - 1), amountPerPage * page);
  }, [data, columns, sort, amountPerPage, currentPage, page]);

  return (
    <div className="overflow-hidden shadow-md 2xl:rounded-lg">
      <div className="min-w-full overflow-hidden overflow-x-auto align-middle">
        <table className="min-w-full divide-gray-200">
          <thead>
            <tr>
              {columns.map((column) => (
                <th
                  key={column.key ?? column.dataKey}
                  onClick={() => {
                    if (column.disableSort) return;
                    setSort({
                      key: column.key ?? column.dataKey,
                      order:
                        sort.key === (column.key ?? column.dataKey) && sort.order === 'desc'
                          ? 'asc'
                          : 'desc',
                    });
                  }}
                  className="group cursor-pointer bg-primary-600 px-6 py-3 text-left text-sm font-medium uppercase tracking-wider text-white transition hover:bg-primary-700"
                >
                  <div className="flex items-center justify-between">
                    <span className="truncate">{column.title}</span>
                    {!column.disableSort && sort.key === (column.key ?? column.dataKey) ? (
                      <span className="ml-2 flex-none rounded bg-primary-700 text-gray-100 transition group-hover:bg-primary-600">
                        {sort.order === 'desc' ? (
                          <ChevronDownIcon className="h-5 w-5" aria-hidden="true" />
                        ) : (
                          <ChevronUpIcon className="h-5 w-5" aria-hidden="true" />
                        )}
                      </span>
                    ) : !column.disableSort ? (
                      <span className="invisible ml-2 flex-none rounded text-gray-100 group-hover:visible group-focus:visible">
                        <ChevronDownIcon className="h-5 w-5" aria-hidden="true" />
                      </span>
                    ) : null}
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <Transition
            show={!isLoading}
            as="tbody"
            className="w-full divide-y divide-gray-200 bg-white"
          >
            {processedRows.map((item, index) => (
              <tr key={item[key]} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-body'}>
                {columns.map((column) => (
                  <td
                    key={column.key || column.dataKey}
                    className="whitespace-nowrap px-6 py-4 text-left text-base text-gray-700"
                  >
                    {column.render
                      ? column.render(item)
                      : item[column.dataKey] || column.defaultValue || '-'}
                  </td>
                ))}
              </tr>
            ))}
          </Transition>
        </table>
      </div>
      <Transition
        show={isLoading}
        className="flex w-full items-center justify-center bg-white py-8 px-4"
      >
        <LoadingSpinner color="primary" content={loadingText} />
      </Transition>
      <Transition
        show={!isLoading && (!data || data?.length === 0)}
        className="flex w-full items-center justify-center bg-white py-8 px-4"
      >
        No se encontraron resultados
      </Transition>
      <Pagination
        page={currentPage || page}
        amountPerPage={amountPerPage}
        total={totalDocs || data?.length}
        maxPage={
          (totalDocs || data?.length) % amountPerPage === 0
            ? (totalDocs || data?.length) / amountPerPage
            : Math.floor((totalDocs || data?.length) / amountPerPage) + 1
        }
        onPreviousPage={() => {
          if (onPageChange && currentPage !== undefined) onPageChange(currentPage - 1);
          else setPage(page - 1);
        }}
        onNextPage={() => {
          if (onPageChange && currentPage !== undefined) onPageChange(currentPage + 1);
          else setPage(page + 1);
        }}
      />
    </div>
  );
}
